// SegmentReassignment.js v2.0 - Wired to real Google Sheets segment data
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, AlertTriangle, CheckCircle, RefreshCw, Search,
         Sun, Moon, ArrowLeft, UserMinus, UserPlus, ClipboardList,
         AlertCircle, Loader } from 'lucide-react';

// ===== BACKEND CONFIG =====
// Map Gateway (for reading segments from MAP_SS)
const MAP_GW = 'https://script.google.com/macros/s/AKfycbyFWHLgFOglJ75Y6AGnyme0P00OjFgE_-qrDN9m0spn4HCgcyBpjvMopsB1_l9MDjIctQ/exec';
// GW21 (for writing ReassignLog to Users sheet)
const GW21 = 'https://script.google.com/macros/s/AKfycbwv_5HDf9EhFPi0HhW0zqah5TzR1G3o4Hqo8ytdnq-G2xMuIl9_CbHPVcWCU2T2pgvK/exec';
const GW_SECRET = 'LYTcomm2026ClaudeGatewaySecretKey99';
const MAP_SS = '1tW_3y6OzEMmkPX8JiYIN6m6dgwx291RSwQ2uTN5X8sg';
const USERS_SS = '1OjSak2YJJvbXjyX3FSND_GfaQUZ2IQkFiMRgLuNfqVw';
const SEGMENT_TABS = ['UG1','UG2','UG3','UG16','UG21','UG23','UG24','UG29','UG30','MISC_SEG'];

async function gwCall(url, action, params) {
  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({ secret: GW_SECRET, action, params })
    });
    const text = await resp.text();
    if (text.includes('HREF="') || text.includes('href="')) {
      const m = text.match(/[Hh][Rr][Ee][Ff]="([^"]+)"/);
      if (m) { const r = await fetch(m[1].replace(/&amp;/g, '&')); return JSON.parse(await r.text()); }
    }
    return JSON.parse(text);
  } catch (e) { return { success: false, error: e.message }; }
}

const REASONS = [
  'Contractor quit mid-project', 'Terminated for performance',
  'Capacity/scheduling conflict', 'Safety violation',
  'Insurance lapse', 'Mutual agreement',
  'Reassigning to in-house crew', 'Other',
];

function SegmentReassignment({ setCurrentPage, darkMode, setDarkMode, loggedInUser }) {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [reassignTo, setReassignTo] = useState('');
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [notes, setNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [filterContractor, setFilterContractor] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPhase, setFilterPhase] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [reassignLog, setReassignLog] = useState([]);
  const [loadProgress, setLoadProgress] = useState('');

  useEffect(() => { const c = () => setIsMobile(window.innerWidth < 768); c(); window.addEventListener('resize', c); return () => window.removeEventListener('resize', c); }, []);

  const ap = darkMode ? '#c850c0' : '#0077B6';
  const as2 = darkMode ? '#ff6b35' : '#00b4d8';
  const bg = darkMode ? '#0d1b2a' : '#f0f7ff';
  const card = darkMode ? '#1e293b' : '#ffffff';
  const tx = darkMode ? '#ffffff' : '#0d1b2a';
  const mt = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const bd = darkMode ? '#334155' : '#e2e8f0';
  const grad = darkMode ? 'linear-gradient(135deg,#c850c0,#ff6b35)' : 'linear-gradient(135deg,#0077B6,#00b4d8)';
  const inp = { width: '100%', padding: '12px 14px', fontSize: '15px', border: `1px solid ${bd}`, borderRadius: '8px', backgroundColor: darkMode ? '#0f172a' : '#fff', color: tx, boxSizing: 'border-box', outline: 'none' };

  const showNotif = (msg, type = 'success') => { setNotification({ msg, type }); setTimeout(() => setNotification(null), 4000); };

  // ===== LOAD REAL SEGMENTS FROM ALL UG TABS =====
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load segments from all billing code tabs in parallel
      setLoadProgress('Loading segments...');
      const tabPromises = SEGMENT_TABS.map(tab =>
        gwCall(MAP_GW, 'sheetsRead', { spreadsheetId: MAP_SS, range: `${tab}!A1:AJ5000` }).catch(() => ({ success: false }))
      );
      const tabResults = await Promise.all(tabPromises);

      const allSegments = [];
      tabResults.forEach((result, idx) => {
        if (!result.success || !result.data?.data) return;
        const rows = result.data.data;
        if (rows.length < 2) return;
        const headers = rows[0];
        rows.slice(1).forEach(row => {
          if (!row[0]) return; // skip empty rows
          const obj = {};
          headers.forEach((h, i) => { if (h) obj[h] = row[i] ?? ''; });
          obj._tab = SEGMENT_TABS[idx]; // track source tab for updates
          // Determine current phase based on status progression
          let phase = 'boring';
          if (obj.pulling_status && obj.pulling_status !== '' && obj.pulling_status !== 'Not Started') phase = 'pulling';
          else if (obj.boring_status === 'QC Approved') phase = 'pulling';
          obj._phase = phase;
          // Get the active status and contractor for current phase
          if (phase === 'pulling') {
            obj._status = obj.pulling_status || 'Not Started';
            obj._contractor = obj.pulling_assigned_to || '';
          } else {
            obj._status = obj.boring_status || 'Not Started';
            obj._contractor = obj.boring_assigned_to || '';
          }
          allSegments.push(obj);
        });
      });

      setSegments(allSegments);
      setLoadProgress(`${allSegments.length} segments loaded`);

      // Load reassignment log
      const logResult = await gwCall(GW21, 'sheetsRead', { spreadsheetId: USERS_SS, range: 'ReassignLog!A1:H100' });
      if (logResult.success && logResult.data?.data?.length > 1) {
        const headers = logResult.data.data[0];
        const logs = logResult.data.data.slice(1).filter(r => r[0]).map(row => {
          const obj = {};
          headers.forEach((h, i) => { if (h) obj[h] = row[i] ?? ''; });
          return obj;
        });
        setReassignLog(logs.reverse()); // newest first
      }
    } catch (err) {
      console.error('Load error:', err);
      showNotif('Failed to load segments', 'error');
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ===== FILTER =====
  const filtered = segments.filter(s => {
    if (filterContractor !== 'all') {
      if (filterContractor === '' && s._contractor) return false;
      if (filterContractor !== '' && s._contractor !== filterContractor) return false;
    }
    if (filterStatus !== 'all' && s._status !== filterStatus) return false;
    if (filterPhase !== 'all' && s._phase !== filterPhase) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (s.segment_id || '').toLowerCase().includes(q) ||
             (s.street || '').toLowerCase().includes(q) ||
             (s._contractor || '').toLowerCase().includes(q) ||
             (s.from_structure || '').toLowerCase().includes(q) ||
             (s.to_structure || '').toLowerCase().includes(q);
    }
    return true;
  });

  const toggleSel = id => setSelectedSegments(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const selAll = () => selectedSegments.length === filtered.length ? setSelectedSegments([]) : setSelectedSegments(filtered.map(s => s.segment_id));

  // ===== REASSIGN / UNASSIGN ACTIONS =====
  const doAction = async (type) => {
    const finalReason = reason === 'Other' ? customReason : reason;
    if (!reason || (reason === 'Other' && !customReason)) { showNotif('Please select a reason', 'error'); return; }
    if (type === 'reassign' && !reassignTo) { showNotif('Select a contractor', 'error'); return; }
    setActionLoading(true);

    const userName = loggedInUser?.name || loggedInUser?.email || 'Admin';
    const dateStr = new Date().toISOString().split('T')[0];

    // Log each reassignment to the ReassignLog sheet
    for (const segId of selectedSegments) {
      const seg = segments.find(s => s.segment_id === segId);
      if (!seg) continue;
      const logId = `RL-${Date.now()}-${Math.random().toString(36).slice(2,6)}`;
      await gwCall(GW21, 'sheetsAppend', {
        spreadsheetId: USERS_SS,
        range: 'ReassignLog!A1',
        values: [[logId, dateStr, segId, seg._contractor || '(None)', type === 'reassign' ? reassignTo : '(Unassigned)', finalReason, notes, userName]]
      });
    }

    // Update local state
    const updated = segments.map(s => {
      if (!selectedSegments.includes(s.segment_id)) return s;
      const newContractor = type === 'reassign' ? reassignTo : '';
      const reset = s._status === 'In Progress';
      if (s._phase === 'pulling') {
        return { ...s, pulling_assigned_to: newContractor, _contractor: newContractor, pulling_status: reset ? 'Not Started' : s.pulling_status, _status: reset ? 'Not Started' : s._status };
      }
      return { ...s, boring_assigned_to: newContractor, _contractor: newContractor, boring_status: reset ? 'Not Started' : s.boring_status, _status: reset ? 'Not Started' : s._status };
    });
    setSegments(updated);

    // Update log display
    const newLogs = selectedSegments.map(segId => {
      const seg = segments.find(s => s.segment_id === segId);
      return { date: dateStr, segmentId: segId, fromContractor: seg?._contractor || '(None)', toContractor: type === 'reassign' ? reassignTo : '(Unassigned)', reason: finalReason, actionBy: userName };
    });
    setReassignLog(prev => [...newLogs, ...prev]);

    showNotif(`${selectedSegments.length} segment(s) ${type === 'reassign' ? `reassigned to ${reassignTo}` : 'unassigned'} — logged to Google Sheets`);
    setSelectedSegments([]);
    setShowReassignModal(false); setShowUnassignModal(false);
    setReason(''); setCustomReason(''); setNotes(''); setReassignTo('');
    setActionLoading(false);
  };

  // ===== BADGE HELPERS =====
  const statusBadge = s => {
    const m = { 'Not Started': ['#fef2f2','#dc2626','#7f1d1d','#fca5a5'], 'In Progress': ['#fefce8','#ca8a04','#713f12','#fde047'], Complete: ['#f0fdf4','#16a34a','#14532d','#86efac'], 'QC Approved': ['#ecfdf5','#059669','#0f5132','#6ee7b7'], Issue: ['#fff7ed','#ea580c','#7c2d12','#fdba74'] };
    const c = m[s] || m['Not Started'];
    return { padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, backgroundColor: darkMode ? c[2] : c[0], color: darkMode ? c[3] : c[1], display: 'inline-block' };
  };
  const phaseBadge = p => {
    const m = { boring: ['#eff6ff','#2563eb','#1e3a5f','#93c5fd'], pulling: ['#faf5ff','#7c3aed','#3b1f5e','#c4b5fd'], splicing: ['#f0fdf4','#16a34a','#14532d','#86efac'] };
    const c = m[p] || m.boring;
    return { padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, backgroundColor: darkMode ? c[2] : c[0], color: darkMode ? c[3] : c[1], display: 'inline-block', textTransform: 'capitalize' };
  };

  const selData = segments.filter(s => selectedSegments.includes(s.segment_id));
  const totalFt = selData.reduce((a, s) => a + (parseInt(s.footage) || 0), 0);
  const uniqCon = [...new Set(segments.map(s => s._contractor).filter(Boolean))].sort();
  const allContractors = [...uniqCon, 'LYT Crew #1', 'LYT Crew #2', 'LYT Splice Crew'].filter((v, i, a) => a.indexOf(v) === i).sort();

  // ===== MODAL =====
  const Modal = ({ title, icon, onSubmit, label, color }) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.6)', padding: 20 }} onClick={() => { setShowReassignModal(false); setShowUnassignModal(false); }}>
      <div style={{ backgroundColor: card, borderRadius: 16, maxWidth: 520, width: '100%', maxHeight: '85vh', overflowY: 'auto', border: `1px solid ${bd}` }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '24px 28px', borderBottom: `1px solid ${bd}`, display: 'flex', alignItems: 'center', gap: 12 }}>{icon}<h2 style={{ fontSize: 20, fontWeight: 700, color: tx, margin: 0 }}>{title}</h2></div>
        <div style={{ padding: '24px 28px' }}>
          <div style={{ backgroundColor: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 20, border: `1px solid ${bd}` }}>
            <p style={{ fontSize: 13, color: mt, margin: '0 0 6px' }}>Selected: <strong style={{ color: tx }}>{selectedSegments.length} segment(s)</strong></p>
            <p style={{ fontSize: 13, color: mt, margin: 0 }}>Total footage: <strong style={{ color: tx }}>{totalFt.toLocaleString()} LF</strong></p>
            {selData.some(s => s._status === 'In Progress') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '8px 12px', backgroundColor: darkMode ? 'rgba(234,179,8,0.15)' : '#fefce8', borderRadius: 8, border: `1px solid ${darkMode ? 'rgba(234,179,8,0.3)' : '#fde68a'}` }}>
                <AlertTriangle size={14} color="#ca8a04" />
                <span style={{ fontSize: 12, color: darkMode ? '#fde047' : '#a16207' }}>{selData.filter(s => s._status === 'In Progress').length} in-progress segment(s) will reset to "Not Started"</span>
              </div>
            )}
          </div>
          {showReassignModal && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: tx, marginBottom: 6 }}>Reassign To</label>
              <select value={reassignTo} onChange={e => setReassignTo(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Select contractor or crew...</option>
                {allContractors.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: tx, marginBottom: 6 }}>Reason *</label>
            <select value={reason} onChange={e => setReason(e.target.value)} style={{ ...inp, cursor: 'pointer' }}><option value="">Select reason...</option>{REASONS.map(r => <option key={r} value={r}>{r}</option>)}</select>
          </div>
          {reason === 'Other' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: tx, marginBottom: 6 }}>Specify Reason</label>
              <input value={customReason} onChange={e => setCustomReason(e.target.value)} placeholder="Enter reason..." style={inp} />
            </div>
          )}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: tx, marginBottom: 6 }}>Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Additional details..." style={{ ...inp, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => { setShowReassignModal(false); setShowUnassignModal(false); }} style={{ flex: 1, padding: 14, backgroundColor: darkMode ? '#374151' : '#f1f5f9', color: tx, border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={onSubmit} disabled={actionLoading} style={{ flex: 1, padding: 14, background: color, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: actionLoading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {actionLoading ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Processing...</> : label}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ===== MAIN RENDER =====
  return (
    <div style={{ minHeight: '100vh', backgroundColor: bg }}>
      <div style={{ height: 4, background: 'linear-gradient(90deg,#0077B6,#00b4d8,#28a745)' }} />
      <div style={{ backgroundColor: darkMode ? '#112240' : '#fff', padding: '6px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${bd}` }}>
        <button onClick={() => setCurrentPage('admin-dashboard')} style={{ display: 'flex', alignItems: 'center', gap: 6, backgroundColor: 'transparent', border: 'none', color: mt, cursor: 'pointer', fontSize: 14, padding: 8, minHeight: 44 }}><ArrowLeft size={16} /> Back to Admin</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {loadProgress && !loading && <span style={{ fontSize: 12, color: mt }}>{loadProgress}</span>}
          <button onClick={loadData} style={{ backgroundColor: 'transparent', border: 'none', color: mt, cursor: 'pointer', padding: 8, minHeight: 44 }}><RefreshCw size={16} /></button>
          {setDarkMode && <button onClick={() => setDarkMode(!darkMode)} style={{ backgroundColor: 'transparent', border: 'none', color: mt, cursor: 'pointer', padding: '8px 12px', minHeight: 44 }}>{darkMode ? <Sun size={18} /> : <Moon size={18} />}</button>}
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '20px 16px' : '32px 24px' }}>
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: isMobile ? 24 : 30, fontWeight: 800, color: tx, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 12 }}><RefreshCw size={28} color={ap} /> Segment Reassignment</h1>
          <p style={{ fontSize: 15, color: mt, margin: 0 }}>Unassign or reassign construction segments — {segments.length} segments across {SEGMENT_TABS.length} billing tabs</p>
        </div>

        {notification && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px', borderRadius: 10, marginBottom: 20, backgroundColor: notification.type === 'error' ? (darkMode ? 'rgba(239,68,68,0.15)' : '#fef2f2') : (darkMode ? 'rgba(34,197,94,0.15)' : '#f0fdf4'), border: `1px solid ${notification.type === 'error' ? (darkMode ? 'rgba(239,68,68,0.3)' : '#fecaca') : (darkMode ? 'rgba(34,197,94,0.3)' : '#bbf7d0')}`, color: notification.type === 'error' ? (darkMode ? '#fca5a5' : '#dc2626') : (darkMode ? '#86efac' : '#16a34a') }}>
            {notification.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
            <span style={{ fontSize: 14, fontWeight: 500 }}>{notification.msg}</span>
          </div>
        )}

        {/* Action bar */}
        {selectedSegments.length > 0 && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '16px 20px', backgroundColor: darkMode ? 'rgba(0,119,182,0.1)' : '#eff6ff', borderRadius: 12, marginBottom: 20, border: `1px solid ${darkMode ? 'rgba(0,119,182,0.2)' : '#bfdbfe'}`, alignItems: 'center' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: ap }}>{selectedSegments.length} selected ({totalFt.toLocaleString()} LF)</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => { setReason(''); setNotes(''); setShowUnassignModal(true); }} style={{ padding: '10px 20px', backgroundColor: darkMode ? '#7f1d1d' : '#fef2f2', color: darkMode ? '#fca5a5' : '#dc2626', border: `1px solid ${darkMode ? '#991b1b' : '#fecaca'}`, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, minHeight: 44 }}><UserMinus size={16} /> Unassign</button>
            <button onClick={() => { setReason(''); setReassignTo(''); setNotes(''); setShowReassignModal(true); }} style={{ padding: '10px 20px', background: grad, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, minHeight: 44 }}><UserPlus size={16} /> Reassign</button>
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          <div style={{ flex: '1 1 200px', position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: mt }} />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search segment ID, street, contractor..." style={{ ...inp, paddingLeft: 36 }} />
          </div>
          <select value={filterContractor} onChange={e => setFilterContractor(e.target.value)} style={{ ...inp, width: 'auto', minWidth: 180, cursor: 'pointer' }}>
            <option value="all">All Contractors ({uniqCon.length})</option>
            <option value="">(Unassigned)</option>
            {uniqCon.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterPhase} onChange={e => setFilterPhase(e.target.value)} style={{ ...inp, width: 'auto', minWidth: 120, cursor: 'pointer' }}>
            <option value="all">All Phases</option><option value="boring">Boring</option><option value="pulling">Pulling</option>
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inp, width: 'auto', minWidth: 140, cursor: 'pointer' }}>
            <option value="all">All Statuses</option><option value="Not Started">Not Started</option><option value="In Progress">In Progress</option><option value="Complete">Complete</option><option value="QC Approved">QC Approved</option><option value="Issue">Issue</option>
          </select>
        </div>

        {/* Segment count */}
        <div style={{ fontSize: 13, color: mt, marginBottom: 12 }}>Showing {filtered.length} of {segments.length} segments</div>

        {/* Segments table */}
        <div style={{ backgroundColor: card, borderRadius: 12, border: `1px solid ${bd}`, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 60, textAlign: 'center' }}>
              <Loader size={32} color={ap} style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{ color: mt, marginTop: 12 }}>Loading real segments from Google Sheets...</p>
              <p style={{ color: mt, fontSize: 12 }}>{loadProgress}</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 850 }}>
                <thead>
                  <tr style={{ backgroundColor: darkMode ? '#0f172a' : '#f8fafc' }}>
                    <th style={{ padding: '12px 14px', textAlign: 'left', borderBottom: `1px solid ${bd}` }}><input type="checkbox" checked={selectedSegments.length === filtered.length && filtered.length > 0} onChange={selAll} style={{ width: 18, height: 18, cursor: 'pointer' }} /></th>
                    {['Segment ID','From → To','Section','Street','Footage','Code','Phase','Status','Contractor'].map(h => (
                      <th key={h} style={{ padding: '12px 14px', textAlign: 'left', borderBottom: `1px solid ${bd}`, fontSize: 12, fontWeight: 700, color: mt, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 200).map(seg => (
                    <tr key={seg.segment_id} onClick={() => toggleSel(seg.segment_id)} style={{ backgroundColor: selectedSegments.includes(seg.segment_id) ? (darkMode ? 'rgba(0,119,182,0.1)' : '#eff6ff') : 'transparent', borderBottom: `1px solid ${bd}`, cursor: 'pointer' }}>
                      <td style={{ padding: '12px 14px' }}><input type="checkbox" checked={selectedSegments.includes(seg.segment_id)} readOnly style={{ width: 18, height: 18, cursor: 'pointer' }} /></td>
                      <td style={{ padding: '12px 14px', fontSize: 12, fontFamily: 'monospace', color: ap, fontWeight: 600 }}>{seg.segment_id}</td>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: tx }}>{seg.from_structure || '?'} → {seg.to_structure || '?'}</td>
                      <td style={{ padding: '12px 14px', fontSize: 14, color: tx, fontWeight: 600 }}>{seg.section || '—'}</td>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: mt }}>{seg.street || '—'}</td>
                      <td style={{ padding: '12px 14px', fontSize: 14, color: tx, fontWeight: 600 }}>{parseInt(seg.footage) || 0} LF</td>
                      <td style={{ padding: '12px 14px', fontSize: 12, fontFamily: 'monospace', color: mt }}>{seg.bore_code || seg._tab}</td>
                      <td style={{ padding: '12px 14px' }}><span style={phaseBadge(seg._phase)}>{seg._phase}</span></td>
                      <td style={{ padding: '12px 14px' }}><span style={statusBadge(seg._status)}>{seg._status}</span></td>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: seg._contractor ? tx : mt, fontStyle: seg._contractor ? 'normal' : 'italic' }}>{seg._contractor || 'Unassigned'}</td>
                    </tr>
                  ))}
                  {filtered.length > 200 && (
                    <tr><td colSpan={10} style={{ padding: '14px 20px', textAlign: 'center', color: mt, fontSize: 13 }}>Showing first 200 of {filtered.length} — use filters to narrow results</td></tr>
                  )}
                  {filtered.length === 0 && <tr><td colSpan={10} style={{ padding: 40, textAlign: 'center', color: mt }}>No segments match your filters</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Reassignment Log */}
        {reassignLog.length > 0 && (
          <div style={{ marginTop: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: tx, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}><ClipboardList size={20} color={as2} /> Reassignment History ({reassignLog.length})</h2>
            <div style={{ backgroundColor: card, borderRadius: 12, border: `1px solid ${bd}`, overflow: 'hidden' }}>
              {reassignLog.slice(0, 20).map((log, i) => (
                <div key={i} style={{ padding: '14px 20px', borderBottom: i < Math.min(reassignLog.length, 20) - 1 ? `1px solid ${bd}` : 'none', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: mt, minWidth: 85 }}>{log.date}</span>
                  <span style={{ fontSize: 12, fontFamily: 'monospace', color: ap, fontWeight: 600 }}>{log.segmentId}</span>
                  <span style={{ fontSize: 13, color: darkMode ? '#fca5a5' : '#dc2626', textDecoration: 'line-through' }}>{log.fromContractor}</span>
                  <ArrowRight size={14} color={mt} />
                  <span style={{ fontSize: 13, color: darkMode ? '#86efac' : '#16a34a', fontWeight: 600 }}>{log.toContractor}</span>
                  <span style={{ fontSize: 12, color: mt, fontStyle: 'italic' }}>{log.reason}</span>
                  <span style={{ fontSize: 12, color: mt, marginLeft: 'auto' }}>by {log.actionBy}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showUnassignModal && <Modal title="Unassign Segments" icon={<UserMinus size={22} color="#dc2626" />} onSubmit={() => doAction('unassign')} label="Unassign Selected" color="#dc2626" />}
      {showReassignModal && <Modal title="Reassign Segments" icon={<UserPlus size={22} color={ap} />} onSubmit={() => doAction('reassign')} label="Reassign Selected" color={grad} />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default SegmentReassignment;
